import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Box, Badge, Tooltip, Collapse } from "@mui/material";
import { Droppable } from "@hello-pangea/dnd";
import {
  PlaceOutlined,
  ExpandMoreOutlined,
  ExpandLessOutlined,
  SearchOutlined,
} from "@mui/icons-material";
import { dt } from "../../../../shared/themes/redesignTokens";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { useAppSelector } from "../../../../shared/hooks/sharedHooks";
import api from "../../../../core/api";
import { Location } from "../../models/models";
import SidebarLocationRow from "./SidebarLocationRow";

interface LocationsSidebarSectionProps {
  collapsed: boolean;
  sectionCollapsed: boolean;
  onToggleSectionCollapse: () => void;
  onLocationsLoaded?: (locations: Location[]) => void;
}

const getCustomersConfig = () => ({
  baseURL: (window as any).app.config.customersApiBaseUrl,
});

const LocationsSidebarSection: React.FC<LocationsSidebarSectionProps> = ({
  collapsed,
  sectionCollapsed,
  onToggleSectionCollapse,
  onLocationsLoaded,
}) => {
  const { translate } = useTranslation("operations");
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // Read machines from Redux to compute per-location machine counts
  const { machines: machinesByType } = useAppSelector(
    (state) => (state as any).customerMachinery
  );

  const machineCountByLocationId = useMemo(() => {
    const counts: Record<string, number> = {};
    const allMachines = Object.values(machinesByType || {}).flat() as any[];
    allMachines.forEach((m: any) => {
      if (m.locationId) {
        counts[m.locationId] = (counts[m.locationId] || 0) + 1;
      }
    });
    return counts;
  }, [machinesByType]);

  // Fetch locations from Customers API
  const fetchLocations = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get<any[]>(
        "/v1/locations",
        getCustomersConfig()
      );
      const raw = res.data || [];
      // API returns hierarchical data with 'childLocations' — map to our 'children' property
      const data: Location[] = raw.map((parent: any) => ({
        ...parent,
        children: (parent.childLocations || []).map((child: any) => ({
          ...child,
          parentLocationName: parent.name,
        })),
      }));
      setLocations(data);
      // Build flat list for parent component's lookup map
      const flat = data.flatMap((parent: Location) => [
        parent,
        ...(parent.children || []),
      ]);
      onLocationsLoaded?.(flat);
      // Auto-expand all parents that have children on first load
      const parentIds = new Set<string>();
      data.forEach((loc: Location) => {
        if ((loc.children || []).length > 0) {
          parentIds.add(loc.id);
        }
      });
      setExpandedIds(parentIds);
    } catch {
      // Silently fail — locations are optional
      setLocations([]);
    } finally {
      setLoading(false);
    }
  }, [onLocationsLoaded]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  // API returns hierarchical data, already mapped in fetchLocations — use directly
  const tree = useMemo(() => locations, [locations]);

  // Filter by search term
  const filteredTree = useMemo(() => {
    if (!searchTerm.trim()) return tree;
    const term = searchTerm.toLowerCase().trim();

    return tree
      .map((parent) => {
        const matchingChildren = (parent.children || []).filter((child) =>
          child.name.toLowerCase().includes(term)
        );
        const parentMatches = parent.name.toLowerCase().includes(term);

        if (parentMatches || matchingChildren.length > 0) {
          return {
            ...parent,
            children: parentMatches ? parent.children : matchingChildren,
          };
        }
        return null;
      })
      .filter(Boolean) as Location[];
  }, [tree, searchTerm]);

  const totalCount = locations.reduce(
    (sum, loc) => sum + 1 + (loc.children || []).length,
    0
  );

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  /** Count all machines under a parent (parent + children) using Redux data */
  const getParentMachineCount = (loc: Location): number => {
    const own = machineCountByLocationId[loc.id] || 0;
    const childCount = (loc.children || []).reduce(
      (sum, c) => sum + (machineCountByLocationId[c.id] || 0),
      0
    );
    return own + childCount;
  };

  // ---- Collapsed sidebar (48px) ----
  if (collapsed) {
    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "8px 0",
          gap: "4px",
        }}
      >
        <Tooltip title={translate("locationSidebar.title")} placement="right">
          <Badge
            badgeContent={totalCount}
            color="primary"
            max={999}
            sx={{
              "& .MuiBadge-badge": {
                fontFamily: dt.fontMono,
                fontSize: "9px",
                fontWeight: 700,
                minWidth: "16px",
                height: "16px",
                padding: "0 4px",
              },
            }}
          >
            <Box
              sx={{
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: dt.rowHover,
              }}
            >
              <PlaceOutlined sx={{ fontSize: 18, color: dt.blue }} />
            </Box>
          </Badge>
        </Tooltip>
      </Box>
    );
  }

  // ---- Section collapsed (within expanded sidebar) ----
  if (sectionCollapsed) {
    return (
      <Box
        onClick={onToggleSectionCollapse}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 16px", /* spec: section horizontal + vertical padding */
          cursor: "pointer",
          transition: "background-color 0.15s ease",
          flexShrink: 0,
          "&:hover": { backgroundColor: "#F7F5F0" /* spec: input-bg hover */ },
        }}
      >
        <Box
          sx={{
            fontFamily: dt.fontMono, /* spec: JetBrains Mono */
            fontSize: "11px",
            fontWeight: 600,
            color: "#8C9099", /* spec: text-muted */
            textTransform: "uppercase",
            letterSpacing: "0.1em", /* spec: section header */
          }}
        >
          {translate("locationSidebar.title")}
        </Box>
        <ExpandMoreOutlined
          sx={{ fontSize: 14, color: "#8C9099" /* spec: text-muted */, flexShrink: 0 }}
        />
      </Box>
    );
  }

  // ---- Expanded section ----
  // Global draggable index counter across all location rows (same as MachineSidebar)
  let draggableIndex = 0;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
      }}
    >
      {/* Section header — spec: JetBrains Mono 11px/600 uppercase */}
      <Box
        onClick={onToggleSectionCollapse}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 16px", /* spec: section horizontal + vertical padding */
          flexShrink: 0,
          cursor: "pointer",
          "&:hover": { backgroundColor: "#F7F5F0" /* spec: input-bg hover */ },
          transition: "background-color 0.15s ease",
        }}
      >
        <Box
          sx={{
            fontFamily: dt.fontMono, /* spec: JetBrains Mono */
            fontSize: "11px",
            fontWeight: 600,
            color: "#8C9099", /* spec: text-muted */
            textTransform: "uppercase",
            letterSpacing: "0.1em", /* spec: section header */
          }}
        >
          {translate("locationSidebar.title")}
        </Box>
        <ExpandLessOutlined sx={{ fontSize: 14, color: "#8C9099" /* spec: text-muted */ }} />
      </Box>

      {/* Search input — spec: #F7F5F0 bg, 6px radius, 8px 12px padding */}
      <Box sx={{ padding: "0 16px 8px" /* spec: section horizontal padding */, flexShrink: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            padding: "8px 12px", /* spec: search input padding */
            borderRadius: "6px", /* spec: search input radius */
            border: "none",
            backgroundColor: "#F7F5F0", /* spec: input-bg */
            transition: "box-shadow 0.15s ease",
            "&:focus-within": { boxShadow: "0 0 0 1.5px #3B82F6" /* spec: focus ring */ },
          }}
        >
          <SearchOutlined
            sx={{ fontSize: 12, color: "#8C9099" /* spec: text-muted */, flexShrink: 0 }}
          />
          <Box
            component="input"
            value={searchTerm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSearchTerm(e.target.value)
            }
            placeholder={translate("locationSidebar.search")}
            sx={{
              border: "none",
              outline: "none",
              backgroundColor: "transparent",
              fontFamily: dt.font,
              fontSize: "12px", /* spec: sidebar search placeholder */
              fontWeight: 400,
              color: "#0C1117", /* spec: text-primary */
              width: "100%",
              padding: 0,
              "&::placeholder": { color: "#8C9099" /* spec: text-muted */ },
            }}
          />
        </Box>
      </Box>

      {/* Location tree wrapped in Droppable (source only — isDropDisabled) */}
      <Box sx={{ padding: "0 16px" /* spec: section horizontal padding */ }}>
        <Droppable droppableId="sidebar-locations" type="PERSON" isDropDisabled>
          {(provided) => (
            <Box
              ref={provided.innerRef}
              {...provided.droppableProps}
              sx={{ maxHeight: "280px", overflowY: "auto", overflowX: "hidden" }}
            >
              {loading && locations.length === 0 ? (
                <Box
                  sx={{
                    padding: "24px 0",
                    fontFamily: dt.font,
                    fontSize: "12px",
                    color: "#8C9099", /* spec: text-muted */
                    textAlign: "center",
                  }}
                >
                  ...
                </Box>
              ) : filteredTree.length === 0 ? (
                <Box
                  sx={{
                    padding: "24px 0",
                    fontFamily: dt.font,
                    fontSize: "12px",
                    color: "#8C9099", /* spec: text-muted */
                    textAlign: "center",
                  }}
                >
                  {translate("locationSidebar.noLocations")}
                </Box>
            ) : (
              filteredTree.map((parent) => {
                const hasChildren = (parent.children || []).length > 0;
                const isExpanded = expandedIds.has(parent.id);
                const parentMachineCount = getParentMachineCount(parent);

                // Parent row (draggable)
                const parentIdx = draggableIndex++;
                const parentRow = (
                  <Box key={parent.id}>
                    {/* Clickable expand/collapse wrapper around the draggable row */}
                    <Box
                      onClick={hasChildren ? () => toggleExpand(parent.id) : undefined}
                      sx={{ cursor: hasChildren ? "pointer" : "default" }}
                    >
                      <SidebarLocationRow
                        locationId={parent.id}
                        locationName={parent.name}
                        machineCountLabel={`${parentMachineCount}`}
                        draggableIndex={parentIdx}
                      />
                    </Box>

                    {/* Children with collapse animation */}
                    {hasChildren && (
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        {(parent.children || []).map((child) => {
                          const childIdx = draggableIndex++;
                          return (
                            <SidebarLocationRow
                              key={child.id}
                              locationId={child.id}
                              locationName={child.name}
                              machineCountLabel={`${machineCountByLocationId[child.id] || 0} ${
                                (machineCountByLocationId[child.id] || 0) === 1 ? "machine" : "machines"
                              }`}
                              draggableIndex={childIdx}
                              isChild
                            />
                          );
                        })}
                      </Collapse>
                    )}
                  </Box>
                );

                return parentRow;
              })
            )}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </Box>

      {/* Drag hint — spec: Sora 11px/400, #8C9099, centered */}
      <Box
        sx={{
          padding: "4px 16px",
          fontFamily: dt.font,
          fontSize: "11px",
          fontWeight: 400,
          color: "#8C9099", /* spec: text-muted */
          textAlign: "center",
          flexShrink: 0,
        }}
      >
        {translate("locationSidebar.dragToAssign")}
      </Box>
    </Box>
  );
};

export default LocationsSidebarSection;
